import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import DataFrameTableList from 'data-frames/table-list/DataFrameTableList';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_15, FONT_PROXIMA_17} from 'styles/FontConstants';
import {FONT_PROXIMA_13, FONT_PROXIMA_16} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import LoadingOverlay from 'ui-library/LoadingOverlay';

export interface StepConfigurationViewTemplateProps {
  children: ReactNode;
  clientStateId: string;
  dataFrames: Array<DataFrame>;
  defaultDataFrameTableMode?: DataFrameTableMode;
  disableFinalizeButton: boolean;
  disableReason?: React.ReactNode;
  finalizeButtonText: string;
  finalizingLoadingMessage?: string;
  finalizeButtonTip?: string;
  isFinalizing?: boolean;
  isLoadingPreview?: boolean;
  messages: Array<InlineBannerProps>;
  /** Options for what is shown when there's no preview. */
  noPreviewPlaceholderOptions?: {
    title: string;
    subtitle: string;
  };
  onCancel: (() => void) | null;
  onFinalize: (() => void) | null;
  title: string;
  toolbar?: ReactNode;
  // Additional content to render in the right pane
  renderAdditionalPreviewContent?: () => ReactNode;
}

export function NoPreviewPlaceholder({title, subtitle}: {title: string; subtitle?: string}): JSX.Element {
  return (
    <div className={css(noPreviewPlaceholderStyles.noPreviewPlaceholderWrapper)}>
      <img
        alt="preview-placeholder"
        className={css(noPreviewPlaceholderStyles.noPreviewPlaceholderImage)}
        width={55}
        src={staticURL('/static/img/analyses/configuration_view_placeholder.svg')}
      />
      <p className={css(noPreviewPlaceholderStyles.noPreviewPlaceholderTitle)}>{title}</p>
      {subtitle && <p className={css(noPreviewPlaceholderStyles.noPreviewPlaceholderSubtitle)}>{subtitle}</p>}
    </div>
  );
}

/**
 * A UI skeleton for step configuration flows, including dataset/data frame creation, that ensures consistent
 * layout, but is generic to the steps and logic of any particular step.
 *
 * Usage:
 *
 *  <StepConfigurationViewTemplate
 *    title="Upload Spreadsheet"
 *    onCancel={() => close()}
 *    onFinalize={() => persistDataFrame()}
 *    disableFinalizeButton={file == null}
 *  >
 *    <Stepper>
 *      <StepperStep title="Upload file">...</StepperStep>
 *      <StepperStep title="Select columns">...</StepperStep>
 *      <StepperStep title="Filter rows">...</StepperStep>
 *    </Stepper>
 *  </StepConfigurationViewTemplate>
 *
 */
export default function StepConfigurationViewTemplate(
  props: StepConfigurationViewTemplateProps
): JSX.Element {
  const hasDataFrames = props.dataFrames != null && props.dataFrames.length > 0;
  const hasPreview = hasDataFrames || props.renderAdditionalPreviewContent != null;

  const preview = hasPreview ? (
    <>
      {hasDataFrames && (
        <DataFrameTableList
          actionOrigin={ActionOrigin.ANALYSIS_TOOL}
          clientStateId={props.clientStateId}
          dataFrames={props.dataFrames}
          defaultTableMode={props.defaultDataFrameTableMode}
          renderIsExcludedColumn={false}
        />
      )}
      {props.renderAdditionalPreviewContent && props.renderAdditionalPreviewContent()}
    </>
  ) : (
    props.noPreviewPlaceholderOptions && (
      <NoPreviewPlaceholder
        title={props.noPreviewPlaceholderOptions.title}
        subtitle={props.noPreviewPlaceholderOptions.subtitle}
      />
    )
  );

  return (
    <div
      className={css(stepConfigurationViewTemplateStyles.view)}
      {...getTestProps({component: 'StepConfigurationViewTemplate', element: 'root'})}
    >
      <div className={css(stepConfigurationViewTemplateStyles.leftPane)}>
        {props.toolbar && (
          <div className={css(stepConfigurationViewTemplateStyles.toolbarWrapper)}>{props.toolbar}</div>
        )}
        <div className={classNames(css(stepConfigurationViewTemplateStyles.steps), 'scrollbar-override')}>
          <div className={css(stepConfigurationViewTemplateStyles.title)}>{props.title}</div>
          {props.isFinalizing ? (
            <div className={css(stepConfigurationViewTemplateStyles.leftPaneThrobberWrapper)}>
              <CenteredHorizontalThrobber />
              {props.finalizingLoadingMessage != null && (
                <span className={css(stepConfigurationViewTemplateStyles.leftPaneThrobberMessage)}>
                  {props.finalizingLoadingMessage}
                </span>
              )}
            </div>
          ) : (
            props.children
          )}
        </div>
        {(props.onCancel || props.onFinalize) && (
          <div className={css(stepConfigurationViewTemplateStyles.leftPaneFooter)}>
            <div>{props.disableFinalizeButton && (props.disableReason ?? null)}</div>
            <div>
              {props.onCancel && (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  className={css(stepConfigurationViewTemplateStyles.confirmationButton)}
                  disabled={props.isFinalizing}
                  onClick={() => {
                    props.onCancel!();
                  }}
                  size={ButtonSize.MEDIUM}
                  text="Cancel"
                />
              )}
              {props.onFinalize && (
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  className={css(stepConfigurationViewTemplateStyles.confirmationButton)}
                  disabled={props.disableFinalizeButton || props.isFinalizing}
                  onClick={() => {
                    props.onFinalize!();
                  }}
                  size={ButtonSize.MEDIUM}
                  text={props.finalizeButtonText}
                  dataTestAttrs={{component: 'StepConfigurationView', element: 'finalizeButton'}}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div className={css(stepConfigurationViewTemplateStyles.rightPane)}>
        {props.messages.length > 0 && (
          <div className={css(stepConfigurationViewTemplateStyles.bannerStack)}>
            <InlineBannerStack messages={props.messages} />
          </div>
        )}
        <LoadingOverlay isFaded={true} isLoading={props.isLoadingPreview ?? false}>
          {preview}
        </LoadingOverlay>
      </div>
    </div>
  );
}

const STEPS_PADDING = 15;

const noPreviewPlaceholderStyles = StyleSheet.create({
  noPreviewPlaceholderImage: {
    marginBottom: 20,
  },
  noPreviewPlaceholderSubtitle: {
    ...FONT_PROXIMA_13,
    color: Colors.GRAY_DARKER,
    textAlign: 'center',
  },
  noPreviewPlaceholderTitle: {
    ...FONT_PROXIMA_16,
    color: Colors.GRAY_DARKER,
    textAlign: 'center',
  },
  noPreviewPlaceholderWrapper: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
  },
});

export const stepConfigurationViewTemplateStyles = StyleSheet.create({
  bannerStack: {
    margin: 10, // Float over the background in the right pane, matching the data frame tables
  },
  confirmationButton: {
    marginLeft: 10,
  },
  leftPane: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '50%',
    padding: 12,
    paddingLeft: STEPS_PADDING,
    paddingRight: STEPS_PADDING,
  },
  leftPaneThrobberWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  leftPaneThrobberMessage: {
    ...FONT_PROXIMA_15,
    textAlign: 'center',
  },
  leftPaneFooter: {
    alignSelf: 'flex-end',
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Given that the parent element is display: flex, this will make the footer sink to the bottom of the layout
    // when the layout's contents are less than 100% height.
    marginTop: 'auto',
    paddingTop: STEPS_PADDING,
    paddingBottom: STEPS_PADDING,
    width: '100%',
  },
  rightPane: {
    background: Colors.GRAY_LIGHTER,
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
  },
  steps: {
    display: 'block',
    overflowY: 'auto',
    paddingRight: 12,
  },
  title: {
    ...FONT_PROXIMA_17,
    paddingBottom: STEPS_PADDING,
  },
  toolbarWrapper: {
    paddingBottom: STEPS_PADDING,
  },
  view: {
    backgroundColor: Colors.WHITE,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
});
